import React, { useState } from 'react';
import { Plus, X, Check, Trash2, Target, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Folder, Pencil, CalendarClock, RotateCcw } from 'lucide-react';
import { neonQuery } from '../services/api';

// Named palette → resolved colors. Keys are stored verbatim in greg_projects.color.
const PROJECT_COLORS = {
  navy:   { solid: '#003b5c', bg: '#e8f0fe', border: '#c3d6ef' },
  orange: { solid: '#dc582a', bg: '#fff3ed', border: '#fcd5c0' },
  green:  { solid: '#2e7d46', bg: '#e6f4ea', border: '#b7e1c4' },
  purple: { solid: '#6b46c1', bg: '#f3effc', border: '#d9c9f2' },
  teal:   { solid: '#0d7a8a', bg: '#e5f6f8', border: '#b6e2e8' },
  gold:   { solid: '#b8860b', bg: '#fdf6e3', border: '#eaddb0' },
  red:    { solid: '#c0392b', bg: '#fdecea', border: '#f3c1bb' },
};
const COLOR_KEYS = Object.keys(PROJECT_COLORS);
const colorOf = (key) => PROJECT_COLORS[key] || PROJECT_COLORS.navy;

function daysUntil(dateStr, today) {
  if (!dateStr) return null;
  const a = new Date(today + 'T00:00:00');
  const b = new Date(dateStr.slice(0, 10) + 'T00:00:00');
  return Math.round((b - a) / 86400000);
}

function formatTarget(dateStr) {
  return new Date(dateStr.slice(0, 10) + 'T12:00:00')
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Projects tab — categorical holders for goals. Each project card holds tasks you can
 * add, drag between projects, and "Schedule →" into a specific day (the daily-chunk step).
 *
 * Controlled component: parent owns `data` ({projects, tasks}) and `reload()`.
 * `onScheduled` refreshes the parent's weekly view after a chunk is scheduled.
 */
export default function TodoProjects({ data, reload, today, scheduleDays, formatDay, onScheduled }) {
  const projects = data?.projects || [];
  const tasks = data?.tasks || [];

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('navy');
  const [editingId, setEditingId] = useState(null);   // project card in edit mode
  const [taskDrafts, setTaskDrafts] = useState({});    // { [projectId]: draftText }
  const [editTaskId, setEditTaskId] = useState(null);  // task in inline-rename mode
  const [editTaskDraft, setEditTaskDraft] = useState('');
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dropProjectId, setDropProjectId] = useState(null);

  const tasksFor = (pid) => tasks.filter(t => t.project_id === pid);

  // ── Project actions ──
  const createProject = async () => {
    const name = newName.trim();
    if (!name) return;
    setNewName('');
    await neonQuery('greg_project_create', { name, color: newColor });
    reload();
  };
  const updateProject = async (id, patch) => { await neonQuery('greg_project_update', { id, ...patch }); reload(); };
  const deleteProject = async (id, name) => {
    if (!window.confirm(`Delete project "${name}" and all its tasks? (Scheduled day tasks stay put.)`)) return;
    setEditingId(null);
    await neonQuery('greg_project_delete', { id });
    reload();
  };
  const moveProject = async (id, dir) => {
    const ids = projects.map(p => p.id);
    const idx = ids.indexOf(id);
    const swap = dir === 'left' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await neonQuery('greg_projects_reorder', { ids });
    reload();
  };

  // ── Task actions ──
  const addTask = async (pid) => {
    const text = (taskDrafts[pid] || '').trim();
    if (!text) return;
    setTaskDrafts(d => ({ ...d, [pid]: '' }));
    await neonQuery('greg_project_task_add', { project_id: pid, text });
    reload();
  };
  const toggleTask = async (t) => { await neonQuery('greg_project_task_toggle', { id: t.id, completed: !t.completed }); reload(); };
  const removeTask = async (id) => { await neonQuery('greg_project_task_remove', { id }); reload(); };
  const startEdit = (t) => { setEditTaskId(t.id); setEditTaskDraft(t.text); };
  const commitEdit = async (t) => {
    const text = editTaskDraft.trim();
    setEditTaskId(null);
    if (!text || text === t.text) return;
    await neonQuery('greg_project_task_rename', { id: t.id, text });
    reload();
  };
  const scheduleTask = async (id, target_date) => {
    await neonQuery('greg_project_task_schedule', { id, target_date });
    reload();
    onScheduled?.();
  };
  const unscheduleTask = async (id) => {
    await neonQuery('greg_project_task_unschedule', { id });
    reload();
    onScheduled?.();
  };

  // ── Priority arrows: nudge an open task up/down within its project (top = top priority) ──
  const moveTaskPriority = async (project, taskId, dir) => {
    const list = tasksFor(project.id);
    const open = list.filter(t => !t.completed);
    const done = list.filter(t => t.completed);
    const idx = open.findIndex(t => t.id === taskId);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= open.length) return;
    [open[idx], open[swap]] = [open[swap], open[idx]];
    const ids = [...open, ...done].map(t => t.id);
    await neonQuery('greg_project_tasks_set_order', { project_id: project.id, ids });
    reload();
  };

  // ── Drag: reorder within a project + move between projects ──
  const dropOnProject = async (e, project, beforeTaskId = null) => {
    e.preventDefault();
    e.stopPropagation();
    setDropProjectId(null);
    const movedId = parseInt(e.dataTransfer.getData('text/plain'));
    setDragTaskId(null);
    if (!movedId) return;
    const targetIds = tasksFor(project.id).map(t => t.id).filter(id => id !== movedId);
    let insertIdx = targetIds.length;
    if (beforeTaskId != null) {
      const bi = targetIds.indexOf(beforeTaskId);
      if (bi >= 0) insertIdx = bi;
    }
    targetIds.splice(insertIdx, 0, movedId);
    await neonQuery('greg_project_tasks_set_order', { project_id: project.id, ids: targetIds });
    reload();
  };

  const doneCount = (pid) => tasksFor(pid).filter(t => t.completed).length;

  return (
    <div>
      {/* Add-project bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <Folder size={16} color="var(--navy)" style={{ flexShrink: 0 }} />
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && createProject()}
          placeholder="Name a project / goal (e.g. Get in shape by Labor Day)…"
          style={{ flex: 1, minWidth: 240, padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }}
        />
        <div style={{ display: 'flex', gap: 5 }}>
          {COLOR_KEYS.map(k => (
            <button key={k} onClick={() => setNewColor(k)} title={k}
              style={{
                width: 20, height: 20, borderRadius: '50%', cursor: 'pointer',
                background: colorOf(k).solid, border: newColor === k ? '2px solid var(--gray-800)' : '2px solid transparent',
                boxShadow: newColor === k ? '0 0 0 2px #fff inset' : undefined,
              }} />
          ))}
        </div>
        <button onClick={createProject} disabled={!newName.trim()}
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 14px', background: newName.trim() ? 'var(--navy)' : 'var(--gray-300)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: newName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }}>
          <Plus size={14} /> Add project
        </button>
      </div>

      {projects.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
          No projects yet. Name your first goal above — then add tasks under it or drag items in from the Backlog.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {projects.map((p, pIdx) => {
            const c = colorOf(p.color);
            const pTasks = tasksFor(p.id);
            const open = pTasks.filter(t => !t.completed);
            const done = pTasks.filter(t => t.completed);
            const ordered = [...open, ...done];
            const dLeft = daysUntil(p.target_date, today);
            const isEditing = editingId === p.id;
            const total = pTasks.length;
            const isDropTarget = dropProjectId === p.id;

            return (
              <div key={p.id}
                onDragOver={e => { e.preventDefault(); setDropProjectId(p.id); }}
                onDragLeave={() => setDropProjectId(cur => cur === p.id ? null : cur)}
                onDrop={e => dropOnProject(e, p)}
                style={{
                  width: 340, background: 'var(--white)', borderRadius: 10,
                  border: isDropTarget ? `2px dashed ${c.solid}` : `1px solid var(--gray-200)`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                }}>
                {/* Header */}
                <div style={{ background: c.bg, borderBottom: `2px solid ${c.solid}`, padding: '10px 12px' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <input defaultValue={p.name} autoFocus
                        onKeyDown={e => { if (e.key === 'Enter') { updateProject(p.id, { name: e.target.value }); setEditingId(null); } }}
                        onBlur={e => { if (e.target.value.trim() && e.target.value !== p.name) updateProject(p.id, { name: e.target.value }); }}
                        style={{ padding: '6px 8px', border: '1px solid var(--gray-300)', borderRadius: 5, fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit', color: 'var(--navy)' }} />
                      <div style={{ display: 'flex', gap: 5 }}>
                        {COLOR_KEYS.map(k => (
                          <button key={k} onClick={() => updateProject(p.id, { color: k })} title={k}
                            style={{ width: 18, height: 18, borderRadius: '50%', cursor: 'pointer', background: colorOf(k).solid, border: p.color === k ? '2px solid var(--gray-800)' : '2px solid transparent' }} />
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Target size={13} color={c.solid} />
                        <input type="date" defaultValue={p.target_date ? p.target_date.slice(0, 10) : ''}
                          onChange={e => updateProject(p.id, { target_date: e.target.value })}
                          style={{ fontSize: 11.5, border: '1px solid var(--gray-300)', borderRadius: 5, padding: '3px 5px', fontFamily: 'inherit', color: 'var(--gray-700)' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button onClick={() => deleteProject(p.id, p.name)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--red, #c0392b)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
                          <Trash2 size={13} /> Delete
                        </button>
                        <button onClick={() => setEditingId(null)}
                          style={{ background: c.solid, color: '#fff', border: 'none', borderRadius: 5, padding: '4px 12px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                          Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button onClick={() => moveProject(p.id, 'left')} disabled={pIdx === 0} title="Move left"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: pIdx === 0 ? 'default' : 'pointer', color: pIdx === 0 ? 'var(--gray-300)' : c.solid, display: 'flex' }}>
                          <ChevronLeft size={15} />
                        </button>
                        <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--navy)', lineHeight: 1.2 }}>{p.name}</div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: total > 0 && done.length === total ? 'var(--green)' : c.solid, background: '#fff', borderRadius: 10, padding: '1px 8px', flexShrink: 0 }}>
                          {done.length}/{total}
                        </span>
                        <button onClick={() => moveProject(p.id, 'right')} disabled={pIdx === projects.length - 1} title="Move right"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: pIdx === projects.length - 1 ? 'default' : 'pointer', color: pIdx === projects.length - 1 ? 'var(--gray-300)' : c.solid, display: 'flex' }}>
                          <ChevronRight size={15} />
                        </button>
                        <button onClick={() => setEditingId(p.id)} title="Edit project"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--gray-500)', display: 'flex' }}>
                          <Pencil size={13} />
                        </button>
                      </div>
                      {p.target_date && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5, fontSize: 11, fontWeight: 600, color: dLeft != null && dLeft < 0 ? 'var(--red, #c0392b)' : c.solid }}>
                          <Target size={12} />
                          by {formatTarget(p.target_date)}
                          {dLeft != null && (
                            <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}>
                              · {dLeft < 0 ? `${-dLeft}d overdue` : dLeft === 0 ? 'today' : `${dLeft}d left`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Task list */}
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 4, minHeight: 40 }}>
                  {ordered.length === 0 && (
                    <div style={{ padding: '14px 8px', textAlign: 'center', color: 'var(--gray-400)', fontSize: 11.5 }}>
                      No tasks yet — add one below or drag from another project.
                    </div>
                  )}
                  {ordered.map(t => (
                    <div key={t.id}
                      draggable={editTaskId !== t.id}
                      onDragStart={e => { e.dataTransfer.setData('text/plain', String(t.id)); setDragTaskId(t.id); }}
                      onDragEnd={() => { setDragTaskId(null); setDropProjectId(null); }}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => dropOnProject(e, p, t.id)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px',
                        background: t.completed ? '#f2f7f3' : 'var(--gray-50)', borderRadius: 6,
                        border: `1px solid ${t.completed ? '#cfe6d6' : 'var(--gray-200)'}`,
                        opacity: dragTaskId === t.id ? 0.4 : t.completed ? 0.75 : 1,
                        cursor: 'grab', transition: 'opacity 0.15s',
                      }}>
                      {!t.completed && (() => {
                        const oi = open.findIndex(x => x.id === t.id);
                        const isFirst = oi === 0, isLast = oi === open.length - 1;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, marginTop: -1 }}>
                            <button onClick={() => moveTaskPriority(p, t.id, 'up')} disabled={isFirst} title="Higher priority"
                              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--gray-300)' : 'var(--gray-500)', display: 'flex' }}>
                              <ChevronUp size={12} />
                            </button>
                            <button onClick={() => moveTaskPriority(p, t.id, 'down')} disabled={isLast} title="Lower priority"
                              style={{ background: 'none', border: 'none', padding: 0, lineHeight: 1, cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--gray-300)' : 'var(--gray-500)', display: 'flex' }}>
                              <ChevronDown size={12} />
                            </button>
                          </div>
                        );
                      })()}
                      <button onClick={() => toggleTask(t)} title={t.completed ? 'Mark not done' : 'Mark done'}
                        style={{ width: 17, height: 17, borderRadius: 4, border: t.completed ? 'none' : '1.5px solid var(--gray-400)', background: t.completed ? 'var(--green)' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        {t.completed && <Check size={11} color="#fff" strokeWidth={3} />}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {editTaskId === t.id ? (
                          <input
                            autoFocus
                            value={editTaskDraft}
                            onChange={e => setEditTaskDraft(e.target.value)}
                            onBlur={() => commitEdit(t)}
                            onKeyDown={e => { if (e.key === 'Enter') commitEdit(t); else if (e.key === 'Escape') setEditTaskId(null); }}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: '1px solid var(--navy)', borderRadius: 4, fontSize: 12.5, fontFamily: 'inherit', outline: 'none' }} />
                        ) : (
                          <div onDoubleClick={() => startEdit(t)} title="Double-click to edit"
                            style={{ fontSize: 12.5, color: t.completed ? 'var(--gray-500)' : 'var(--gray-900)', textDecoration: t.completed ? 'line-through' : 'none', lineHeight: 1.3 }}>
                            {t.text}
                          </div>
                        )}
                        {t.scheduled_date && !t.completed && editTaskId !== t.id && (
                          <button onClick={() => unscheduleTask(t.id)} title="Unschedule (removes the day task)"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 3, background: '#eef3ff', border: '1px solid #d0daf0', borderRadius: 4, padding: '1px 6px', fontSize: 9.5, fontWeight: 600, color: '#3557b7', cursor: 'pointer', fontFamily: 'inherit' }}>
                            <CalendarClock size={10} /> {formatDay(t.scheduled_date.slice(0, 10))}
                            <RotateCcw size={9} style={{ marginLeft: 1 }} />
                          </button>
                        )}
                      </div>
                      {!t.completed && editTaskId !== t.id && (
                        <select value="" onChange={e => { if (e.target.value) scheduleTask(t.id, e.target.value); }}
                          title="Break into a daily chunk — schedule to a day"
                          style={{ fontSize: 9.5, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '2px 3px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)', flexShrink: 0, maxWidth: 74 }}>
                          <option value="" disabled>{t.scheduled_date ? 'Re-sched' : 'Schedule'} →</option>
                          {scheduleDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
                        </select>
                      )}
                      {editTaskId !== t.id && (
                        <button onClick={() => startEdit(t)} title="Edit task"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 1, flexShrink: 0, marginTop: 1, display: 'flex' }}>
                          <Pencil size={12} />
                        </button>
                      )}
                      <button onClick={() => removeTask(t.id)} title="Delete task"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 1, flexShrink: 0, marginTop: 1, display: 'flex' }}>
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add task */}
                <div style={{ padding: '0 10px 10px', display: 'flex', gap: 6 }}>
                  <input
                    value={taskDrafts[p.id] || ''}
                    onChange={e => setTaskDrafts(d => ({ ...d, [p.id]: e.target.value }))}
                    onKeyDown={e => e.key === 'Enter' && addTask(p.id)}
                    placeholder="Add a task…"
                    style={{ flex: 1, minWidth: 0, padding: '6px 9px', border: '1px solid var(--gray-300)', borderRadius: 5, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                  <button onClick={() => addTask(p.id)} disabled={!(taskDrafts[p.id] || '').trim()}
                    style={{ display: 'flex', alignItems: 'center', padding: '6px 9px', background: (taskDrafts[p.id] || '').trim() ? c.solid : 'var(--gray-300)', color: '#fff', border: 'none', borderRadius: 5, cursor: (taskDrafts[p.id] || '').trim() ? 'pointer' : 'default', flexShrink: 0 }}>
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
